<template>
  <div class="plan-calendar__toolbar">
    <v-btn
      size="small"
      variant="text"
      icon="mdi-chevron-left"
      :aria-label="t(navLabel('prev'))"
      @click="emit('prev')"
    />
    <div class="plan-calendar__month">{{ monthLabel }}</div>
    <v-btn
      size="small"
      variant="text"
      icon="mdi-chevron-right"
      :aria-label="t(navLabel('next'))"
      @click="emit('next')"
    />
    <div
      class="plan-calendar__view"
      role="group"
      :aria-label="t('marketing-agent.calendar.view')"
    >
      <v-btn
        v-for="option in viewOptions"
        :key="option"
        class="plan-calendar__view-btn"
        size="small"
        rounded="pill"
        color="primary"
        :variant="view === option ? 'flat' : 'outlined'"
        :aria-pressed="view === option"
        @click="emit('update:view', option)"
      >
        {{ t(`marketing-agent.calendar.${option}`) }}
      </v-btn>
    </div>
    <v-btn
      v-if="hasPlan && canPlanNext"
      class="plan-calendar__create_icon"
      icon="mdi-calendar-edit-outline"
      variant="text"
      color="success"
      size="small"
      :aria-label="t('marketing-agent.calendar.delete-plan')"
      @click="emit('delete-plan')"
    />
    <v-btn
      v-if="(!hasPlan && !planUnknown) || (canPlanNext && isPlanNextIterationEnabled)"
      class="plan-calendar__create"
      :class="!hasPlan ? 'plan-calendar__create_btn' : ''"
      size="small"
      color="primary"
      variant="flat"
      rounded="pill"
      :loading="creating"
      @click="emit('create-plan')"
    >
      {{
        t(
          hasPlan
            ? 'marketing-agent.calendar.next-iteration'
            : 'marketing-agent.calendar.create-plan'
        )
      }}
    </v-btn>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import useFeature from '@/composables/useFeature'
import featureNames from '@/lib/featureNames'

const viewOptions = ['day', 'week', 'month']

const props = defineProps({
  view: {
    type: String,
    required: true,
  },
  monthLabel: {
    type: String,
    required: true,
  },
  hasPlan: {
    type: Boolean,
    default: false,
  },
  // Hides "Create plan" while it is not known whether a plan exists.
  planUnknown: {
    type: Boolean,
    default: false,
  },
  canPlanNext: {
    type: Boolean,
    default: false,
  },
  creating: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:view', 'prev', 'next', 'create-plan', 'delete-plan'])

const { t } = useI18n()
const { getFeature } = useFeature()

function navLabel(direction) {
  if (props.view === 'month') return `marketing-agent.calendar.${direction}-month`
  if (props.view === 'day') return `marketing-agent.calendar.${direction}-day`
  return `marketing-agent.calendar.${direction}`
}

const isPlanNextIterationEnabled = computed(() => {
  const feature = getFeature(featureNames.MARKETING_AGENT)
  return feature?.config?.canPlanNextIteration ?? false
})
</script>

<style scoped>
.plan-calendar__toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 12px;
}

.plan-calendar__month {
  min-width: 148px;
  font-size: 18px;
  font-weight: 600;
  color: #1f1f1f;
}

.plan-calendar__view {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
}

.plan-calendar__view-btn {
  height: 32px;
  text-transform: none;
  letter-spacing: 0;
}

.plan-calendar__create_icon {
  border: 1px solid rgb(var(--v-theme-success));
  margin-left: auto;
  cursor: pointer;
}

.plan-calendar__create_btn {
  margin-left: auto;
}

.plan-calendar__create {
  text-transform: none;
  letter-spacing: 0;
}
</style>
